// Registry of the dashboard tables exposed through the API keys. Single source
// of truth for both the REST `api` and the `mcp` functions: the writable
// columns listed here are the whitelist applied on create/update (everything
// else — ids, timestamps, counters, soft-delete, token columns — stays
// server-managed) and also become the MCP tools' input schema.

export interface JsonSchema {
  type?: string | string[];
  description?: string;
  enum?: readonly (string | null)[];
  /** `http-url`: absolute http(s) only — blocks `javascript:` hrefs. */
  format?: "uuid" | "date-time" | "email" | "http-url";
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  required?: readonly string[];
  additionalProperties?: boolean;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
}

export type CrudAction = "list" | "get" | "create" | "update" | "delete";

export interface ResourceDef {
  /** Plural key, also the MCP tool suffix (`list_leads`). */
  name: string;
  /** Singular for MCP tool names (`get_lead`). */
  singular: string;
  /** First REST path segment (`/v1/blog-posts`). */
  restPath: string;
  table: string;
  label: string;
  actions: readonly CrudAction[];
  fields: Record<string, JsonSchema>;
  /** Fields required on create. */
  required: readonly string[];
}

const str = (description?: string): JsonSchema => ({
  type: ["string", "null"],
  ...(description ? { description } : {}),
});
const uuid = (description?: string): JsonSchema => ({
  type: ["string", "null"],
  format: "uuid",
  ...(description ? { description } : {}),
});
const httpUrl: JsonSchema = { type: ["string", "null"], format: "http-url" };
const dateTime: JsonSchema = { type: ["string", "null"], format: "date-time" };
const bool: JsonSchema = { type: ["boolean", "null"] };
const int: JsonSchema = { type: ["integer", "null"] };
const num: JsonSchema = { type: ["number", "null"] };
const strArray: JsonSchema = {
  type: ["array", "null"],
  items: { type: "string" },
};
const obj: JsonSchema = { type: ["object", "null"] };
const oneOf = (values: readonly string[]): JsonSchema => ({
  type: ["string", "null"],
  enum: [...values, null],
});

const CRUD: readonly CrudAction[] = [
  "list",
  "get",
  "create",
  "update",
  "delete",
];

export const LEAD_STATUSES = [
  "new",
  "contacted",
  "qualified",
  "converted",
  "lost",
  "delivered",
] as const;
export const INTERACTION_TYPES = ["whatsapp", "email", "call", "note"] as const;

export const RESOURCES: readonly ResourceDef[] = [
  {
    name: "leads",
    singular: "lead",
    restPath: "leads",
    table: "leads",
    label: "lead do CRM",
    actions: CRUD,
    // E-mail optional since 20261006120200 (lead promoted from a cold lead).
    required: ["name", "message"],
    fields: {
      name: str(),
      email: { type: ["string", "null"], format: "email" },
      phone: str(),
      message: str(),
      project_type: str(),
      source: str(),
      status: oneOf(LEAD_STATUSES),
      notes: str(),
      next_step: str(),
      next_follow_up_at: dateTime,
      contacted_at: dateTime,
      converted_at: dateTime,
      delivered_at: dateTime,
      lost_at: dateTime,
      lost_reason: str(),
      tags: strArray,
      client_id: uuid(),
    },
  },
  {
    name: "blog_posts",
    singular: "blog_post",
    restPath: "blog-posts",
    table: "blog_posts",
    label: "post do blog",
    actions: CRUD,
    required: ["title", "slug", "content"],
    fields: {
      title: str(),
      slug: str(),
      content: str("Markdown/HTML do post"),
      excerpt: str(),
      cover_image_url: httpUrl,
      category_id: uuid("id de blog_categories"),
      published: bool,
      published_at: dateTime,
    },
  },
  {
    name: "blog_categories",
    singular: "blog_category",
    restPath: "categories",
    table: "blog_categories",
    label: "categoria do blog",
    actions: CRUD,
    required: ["name", "slug"],
    fields: { name: str(), slug: str(), description: str() },
  },
  {
    name: "projects",
    singular: "project",
    restPath: "projects",
    table: "projects",
    label: "projeto do portfólio",
    actions: CRUD,
    required: ["title", "description"],
    fields: {
      title: str(),
      description: str(),
      slug: str(),
      status: oneOf(["draft", "published"]),
      featured: bool,
      display_order: int,
      technologies: strArray,
      image_url: httpUrl,
      project_url: httpUrl,
      github_url: httpUrl,
      case_study: obj,
      client_id: uuid(),
    },
  },
  {
    name: "client_projects",
    singular: "client_project",
    restPath: "client-projects",
    table: "client_projects",
    label: "projeto de cliente (kanban)",
    actions: CRUD,
    required: ["title"],
    fields: {
      title: str(),
      description: str(),
      phase: oneOf(["backlog", "in_progress", "review", "done"]),
      priority: int,
      display_order: int,
      due_date: str("YYYY-MM-DD"),
      budget: num,
      estimated_hours: num,
      logged_hours: num,
      tags: strArray,
      client_name: str(),
      client_email: str(),
      client_id: uuid(),
      lead_id: uuid(),
      assigned_to: uuid(),
      uses_jira_mode: bool,
    },
  },
  {
    name: "analytics_events",
    singular: "analytics_event",
    restPath: "analytics",
    table: "analytics_events",
    label: "evento de analytics",
    actions: ["list", "create"],
    required: ["event_type"],
    fields: {
      event_type: str(),
      page_path: str(),
      referrer: str(),
      user_agent: str(),
      metadata: obj,
    },
  },
  {
    name: "notifications",
    singular: "notification",
    restPath: "notifications",
    table: "admin_notifications",
    label: "notificação do admin",
    actions: ["list"],
    required: [],
    fields: {},
  },
];

/** Columns writable on `lead_interactions` (lead_id comes from the path/arg). */
export const INTERACTION_FIELDS: Record<string, JsonSchema> = {
  type: oneOf(INTERACTION_TYPES),
  content: str(),
  metadata: obj,
};

export function resourceByName(name: string): ResourceDef | undefined {
  return RESOURCES.find((r) => r.name === name);
}

export function resourceByRestPath(path: string): ResourceDef | undefined {
  return RESOURCES.find((r) => r.restPath === path);
}
